import * as d3 from 'd3'
import * as React from 'react'
import styled from 'styled-components'
import { Fonts } from '../../../../common/src/constants'

interface LineChartMargins {
  top: number
  left: number
  bottom: number
  right: number
}

interface LineChartDefaultConfig {
  colorMapper: (series: string) => string
  margins: LineChartMargins
  yLabelFormatter: (value: number) => string
  tooltipFormatter: (d: Date) => string
}

export interface LineChartConfig<D> extends Partial<LineChartDefaultConfig> {
  dateMapper: (row: D) => string
  valueMapper: (row: D) => number
  seriesMapper: (row: D) => string
  onHover?: (row: D) => void
}

interface NestedSeries {
  key: string
  values: { date: Date; value: number }[]
}

type LineChartFullConfig<D> = LineChartConfig<D> & LineChartDefaultConfig

interface Series {
  key: string
  values: DateVal[]
}

interface DateVal {
  date: Date
  value: number
}

const DEFAULT_CONFIG: LineChartDefaultConfig = {
  margins: { top: 4, left: 0, bottom: 32, right: 40 },
  colorMapper: () => '#07b5e3',
  yLabelFormatter: val => '' + Math.round(val),
  tooltipFormatter: d3.timeFormat('%b %d'),
}

const NUM_Y_TICKS = 5
const AXIS_PADDING = 4
const HORIZONTAL_CHART_PADDING = 16
const HOVER_DOT_SELECTOR = '.hover-dot'
const FONT_FAMILY_ATTR = 'font-family'

/**
 * Creates a line chart.
 *
 * Usage:
 *
 * const config = {
 *   dateMapper: function () { ... },
 *   valueMapper: function () { ... },
 *   seriesMapper: function () { ... },
 *   colorMapper: function(seriesName) { ... },
 *   onHover: function(date, data) {
 *     // date is null if nothing is selected
 *     // data is array of { series, value }, undefined if no selection
 *   },
 * }
 *
 * const data = [ ... array of arbitrary data, one element per point on the chart ... ]
 *
 * return <LineChart data={data} config={config} />
 */
export default class LineChart<D> extends React.PureComponent<{
  data: D[]
  dateRange?: [string, string]
  config: LineChartConfig<D>
  onHover: (date?: string, data?: { series: string; value: number }[]) => void
}> {
  private svg?: SVGElement
  private g?: d3.Selection<SVGElement, unknown, null, undefined>
  private overlays?: d3.Selection<SVGGElement, unknown, null, undefined>
  private x?: d3.ScaleTime<number, number>
  private y?: d3.ScaleLinear<number, number>

  render() {
    this.redraw()
    return <LineChartSvg ref={this.setSvgRefBound} />
  }

  private setSvgRefBound = (ref: SVGSVGElement) => {
    this.svg = ref
    this.redraw()
  }

  private redraw() {
    const elem = this.svg
    if (elem == null) {
      return
    }

    const { config, series, dates, xDomain } = this.computeFromProps()

    const margins = config.margins
    const width = elem.clientWidth - margins.left - margins.right
    const height = elem.clientHeight - margins.top - margins.bottom

    if (width <= 0 || height <= 0) {
      const params = { width, height, margins }
      console.warn('LineChart svg invalid: ' + JSON.stringify(params))
      return
    }

    this.createChartArea(width, height, margins)
    this.createScales(width, height, config, xDomain, series)
    this.renderAxes(width, height, config)
    this.renderLines(config, series)
    this.renderOverlays(width, height, config, dates)
  }

  private computeFromProps() {
    const { data, dateRange } = this.props

    const config = Object.assign({}, DEFAULT_CONFIG, this.props.config)

    const dates: Date[] = []
    const mappedData = data
      .map((row: D) => {
        const date = parseMDY(config.dateMapper(row))
        const value = config.valueMapper(row)
        const series = config.seriesMapper(row)

        return { series, date, value }
      })
      .filter(entry => {
        if (isFinite(entry.value)) {
          dates.push(entry.date!)
          return true
        }
        return false
      })

    // d3.group() returns a Map, convert to array format matching old d3.nest() output
    const series = Array.from(
      d3.group(mappedData, d => d.series),
      ([key, values]) => ({ key, values })
    ) as NestedSeries[]
    dates.sort((a, b) => a.getTime() - b.getTime())

    const xDomain: [Date, Date] = dateRange
      ? parseDateRange(dateRange)
      : [d3.min(dates) || new Date('2000-01-01'), d3.max(dates) || new Date()]

    return { config, series, dates, xDomain }
  }

  private createChartArea(width: number, height: number, margins: LineChartMargins) {
    if (!this.g) {
      this.g = d3
        .select(this.svg!)
        .append<SVGElement>('g')
        .attr('transform', `translate(${margins.left},${margins.top})`) as any
      this.g!.append('g')
        .attr('class', 'axis axis-x')
        .attr('transform', 'translate(0,' + height + ')')
      this.g!.append('g').attr('class', 'axis axis-y').attr('fill', '#808080')
      // line shown on hover
      this.g!.append('line')
        .attr('class', 'hover-line')
        .attr('display', 'none')
        .attr('y1', 0)
        .attr('y2', height)
        .attr('stroke', '#808080')
        .attr('stroke-dasharray', '1,1')
        .attr('stroke-width', '1px')
    }
  }

  private renderOverlays(width: number, height: number, config: LineChartFullConfig<D>, dates: Date[]) {
    if (this.g == null) {
      return
    }

    this.overlays = this.g.append('g')

    const hoverInfo = this.overlays.append('g').attr('class', 'hover-info').attr('display', 'none').attr('z-index', 1)
    hoverInfo.append('rect').attr('class', 'hover-info-rect').attr('fill', '#111111').attr('opacity', 0.8)
    hoverInfo
      .append('text')
      .attr('class', 'hover-info-text')
      .attr('fill', '#aaaaaa')
      .attr('font-size', '12px')
      .attr(FONT_FAMILY_ATTR, Fonts.mono)
      .attr('text-anchor', 'middle')

    const self = this
    // overlay for mouse events
    this.overlays
      .append<d3.ContainerElement>('rect')
      .attr('fill', 'none')
      .attr('pointer-events', 'all')
      .attr('width', width)
      .attr('height', height)
      .on('mouseout', () => self.onMouseOut())
      .on('mousemove', function () {
        self.onMouseMove(this, height, config, dates)
      })
  }

  private createScales(
    width: number,
    height: number,
    config: LineChartFullConfig<D>,
    xDomain: [Date, Date],
    series: NestedSeries[]
  ) {
    this.x = d3
      .scaleTime()
      .range([HORIZONTAL_CHART_PADDING, width - HORIZONTAL_CHART_PADDING])
      .domain(xDomain)

    const yExtent = [
      d3.min(series, s => d3.min(s.values, d => d.value)) || 0,
      d3.max(series, s => d3.max(s.values, d => d.value)) || 1,
    ]

    // Minimum Y axis height is 0.01 or approximately 1% of the y-values, whichever is bigger
    const minYExtent = Math.max(0.01, Math.max(Math.abs(yExtent[0]), Math.abs(yExtent[1])) * 0.01)
    if (yExtent[1] - yExtent[0] < minYExtent) {
      const midY = (yExtent[0] + yExtent[1]) / 2
      yExtent[0] = midY - minYExtent / 2
      yExtent[1] = midY + minYExtent / 2
    }

    const yTicks: any[] = d3.ticks(yExtent[0] as number, yExtent[1] as number, NUM_Y_TICKS)
    const yTickIncrement = d3.tickStep(yExtent[0] as number, yExtent[1] as number, NUM_Y_TICKS)

    const yDomain = yExtent
    if (yTicks[0] > yExtent[0]) {
      yDomain[0] = (yTicks[0] as number) - yTickIncrement
    }
    if (yTicks[yTicks.length - 1] < yExtent[1]) {
      yDomain[1] = yTicks[yTicks.length - 1] + yTickIncrement
    }
    this.y = d3.scaleLinear().range([height, 0]).domain(yDomain)
  }

  private renderAxes(width: number, height: number, config: LineChartFullConfig<D>) {
    if (!this.g || !this.x) {
      return
    }

    const customTimeFormat = (d: Date | d3.NumberValue) => {
      const date = d as Date
      if (date.getDate() !== 1) {
        return d3.timeFormat('%b %d')(date)
      } else if (date.getMonth() !== 0) {
        return d3.timeFormat('%b')(date)
      }
      return d3.timeFormat('%Y')(date)
    }

    // x-axis
    const numXTicks = Math.max(3, Math.floor(width / 100))
    const xAxisRenderer = d3.axisBottom(this.x).ticks(numXTicks).tickFormat(customTimeFormat).tickPadding(AXIS_PADDING)
    const xAxis = this.g.select<SVGGElement>('.axis-x').call(xAxisRenderer)
    xAxis.selectAll('text').attr('fill', '#808080').attr(FONT_FAMILY_ATTR, Fonts.mono).attr('font-size', '12px')
    xAxis.select('.domain').remove()
    xAxis.selectAll('.tick line').remove()

    // y-axis
    const yAxisRenderer = d3
      .axisRight(this.y!)
      .ticks(NUM_Y_TICKS)
      .tickSize(width)
      .tickPadding(AXIS_PADDING)
      .tickFormat(config.yLabelFormatter as (domainValue: d3.NumberValue, index: number) => string)
    const yAxis = this.g.select<SVGGElement>('.axis-y').call(yAxisRenderer)
    yAxis.selectAll('text').attr('fill', '#808080').attr(FONT_FAMILY_ATTR, Fonts.mono).attr('font-size', '12px')
    yAxis.select('.domain').remove()
    yAxis.selectAll('.tick line').attr('stroke', '#808080').attr('stroke-dasharray', '1,10')
  }

  private renderLines(config: LineChartFullConfig<D>, series: NestedSeries[]) {
    if (!this.g) {
      return
    }

    const line = d3
      .line<DateVal>()
      .x((d: DateVal) => this.x!(d.date)!)
      .y((d: DateVal) => this.y!(d.value)!)

    // render line graph
    const pathSelection = this.g.selectAll<SVGPathElement, unknown>('.line').data(series)
    pathSelection
      .enter()
      .append('path')
      .merge(pathSelection)
      .attr('class', 'line')
      .attr('fill', 'none')
      .attr('stroke-width', '2px')
      .attr('stroke-linecap', 'round')
      .attr('d', (d: Series) => line(d.values))
      .style('stroke', (d: Series) => config.colorMapper(d.key))
    pathSelection.exit().remove()

    // render dots (shown on hover)
    const hoverDotSelection = this.g.selectAll<SVGCircleElement, unknown>(HOVER_DOT_SELECTOR).data(series)
    hoverDotSelection
      .enter()
      .append('circle')
      .merge(hoverDotSelection)
      .attr('class', 'hover-dot')
      .attr('display', 'none')
      .attr('r', 3)
      .style('fill', (d: Series) => config.colorMapper(d.key))
    hoverDotSelection.exit().remove()
  }

  private onMouseMove(elem: d3.ContainerElement, height: number, config: LineChartFullConfig<D>, dates: Date[]) {
    if (!this.g || !this.x) {
      return
    }

    // Find date to show
    const x0 = this.x.invert(d3.mouse(elem)[0])
    const bound = d3.bisector(d => d).left(dates, x0, 1)
    const i = Math.max(1, Math.min(dates.length - 1, bound))
    const d0 = dates[i - 1]
    const d1 = dates[i]

    if (!x0 || !d0 || !d1 || !x0) {
      return
    }
    const date = x0.getTime() - d0.getTime() > d1.getTime() - x0.getTime() ? d1 : d0

    this.g
      .select('.hover-info')
      .attr('display', 'block')
      .attr('transform', 'translate(' + d3.mouse(elem)[0] + ',' + (d3.mouse(elem)[1] - 8) + ')')
    const textElem = this.g.select('.hover-info-text').text(config.tooltipFormatter(date)).node() as SVGGraphicsElement
    const textBoundingBox = textElem.getBBox()
    // draw box around text
    this.g
      .select('.hover-info-rect')
      .attr('x', textBoundingBox.x - 3)
      .attr('y', textBoundingBox.y - 3)
      .attr('width', textBoundingBox.width + 6)
      .attr('height', textBoundingBox.height + 3)

    const self = this
    const entries: { series: string; value: number }[] = []
    this.g.selectAll<SVGElement, Series>(HOVER_DOT_SELECTOR).each(function (set) {
      const entry = set.values.filter(value => value.date.getTime() === date.getTime())[0]
      if (entry) {
        d3.select(this)
          .attr('display', 'block')
          .attr('transform', 'translate(' + self.x!(entry.date) + ',' + self.y!(entry.value) + ')')
        entries.push({
          series: set.key,
          value: entry.value,
        })
      } else {
        d3.select(this).attr('display', 'none')
      }
    })

    const hoverX = this.x(date)
    this.g.select('.hover-line').attr('display', 'block').attr('x1', hoverX!).attr('x2', hoverX!)

    const dateISO = date.toISOString().substring(0, 10) // yyyy-mm-dd
    if (this.props.onHover) {
      this.props.onHover(dateISO, entries)
    }
  }

  private onMouseOut() {
    if (!this.g) {
      return
    }
    this.g.selectAll(HOVER_DOT_SELECTOR).each(function () {
      d3.select(this).attr('display', 'none')
    })
    this.g.select('.hover-line').attr('display', 'none')
    this.g.select('.hover-info').attr('display', 'none')

    this.setState({ hoverDate: undefined })

    if (this.props.onHover) {
      this.props.onHover(undefined, [])
    }
  }
}

function parseDateRange(dateRange: [string, string]): [Date, Date] {
  return [parseMDY(dateRange[0]), parseMDY(dateRange[1])]
}

function parseMDY(str: string) {
  return new Date(str)
}

const LineChartSvg = styled.svg`
  width: 100%;
  height: 256px;
`
